// React Router route loader for the project view (POC of the loader + TanStack
// "render-as-you-fetch" pattern).
//
// Without this, the project view is render-then-fetch : navigate → RR downloads
// the lazy route module → ProjectViewShell mounts → its useQuery calls fire →
// THEN the network requests start (a waterfall). RR runs this loader IN PARALLEL
// with the lazy module download, so the shared queries kick off at navigation
// time and the TanStack cache is warm by the time the panels mount.
//
// It prefetches into the SAME query keys the components read, so we keep the
// TanStack cache, the office-WebSocket invalidation, and the optimistic
// mutations untouched — the loader only changes WHEN the fetch starts, not who
// owns the data. Fire-and-forget : it never awaits, never throws, never blocks
// navigation (prefetchQuery swallows errors); the components still own
// loading / error / the 401 → /login redirect.
import { queryClient } from '@/lib/query-client.js';
import { apiFetch } from '@/lib/api.js';
import { projectStoriesKey } from '@/components/project-view/task-list/useTaskList.js';

export function projectViewLoader({ params }) {
  const { officeId, projectId } = params;
  if (!officeId || !projectId) return null;

  const prefetch = (queryKey, path) =>
    queryClient.prefetchQuery({ queryKey, queryFn: () => apiFetch(path) });

  // Same keys + paths as ProjectViewShell's useQuery calls (must match exactly
  // so the warm cache is reused, not duplicated).
  prefetch(
    ['office', officeId, 'project', projectId],
    `/api/offices/${officeId}/projects/${projectId}`,
  );
  prefetch(
    ['office', officeId, 'project', projectId, 'missions'],
    `/api/offices/${officeId}/projects/${projectId}/missions`,
  );
  prefetch(
    projectStoriesKey(officeId, projectId),
    `/api/offices/${officeId}/projects/${projectId}/stories`,
  );

  return null;
}
